import { useState } from 'react';
import { useAuth } from '../hooks/useAuth';

/**
 * Prominent banner shown at the top of the app while a super admin is
 * impersonating another user. Clicking "Exit" restores the original session.
 */
export function ImpersonationBanner() {
  const { isImpersonating, user, exitImpersonation } = useAuth();
  const [exiting, setExiting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (!isImpersonating) return null;

  const handleExit = async () => {
    setError(null);
    setExiting(true);
    try {
      await exitImpersonation();
      // Send the super admin back to the Accounts list so they land somewhere
      // meaningful rather than the impersonated user's workspace.
      window.location.assign('/accounts');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to exit impersonation');
      setExiting(false);
    }
  };

  return (
    <div className="fixed top-0 left-0 right-0 z-50 h-11 bg-amber-500 text-navy-950 border-b border-amber-600">
      <div className="max-w-7xl mx-auto h-full px-4 flex items-center justify-between gap-4">
        <div className="flex items-center gap-3 min-w-0">
          <svg className="w-5 h-5 flex-shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v3.75m9-.75a9 9 0 11-18 0 9 9 0 0118 0zm-9 3.75h.008v.008H12v-.008z" />
          </svg>
          <p className="text-sm font-semibold truncate">
            Impersonating {user?.email ?? 'user'} ({user?.role?.replace('_', ' ')}) — all actions are audit-logged.
          </p>
        </div>
        <div className="flex items-center gap-3 flex-shrink-0">
          {error && <span className="text-xs text-red-900">{error}</span>}
          <button
            type="button"
            onClick={handleExit}
            disabled={exiting}
            className="px-3 py-1.5 text-xs font-bold bg-navy-950 text-amber-400 rounded-md hover:bg-navy-900 transition-colors disabled:opacity-50"
          >
            {exiting ? 'Exiting…' : 'Exit impersonation'}
          </button>
        </div>
      </div>
    </div>
  );
}
